import type { AirportDto, FlightDto, StatsDto, UserDto } from '@flightlog/shared';
import { getWebApp } from './telegram';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // тело не JSON — оставляем статус
    }
    throw new ApiError(response.status, message);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

let authPromise: Promise<UserDto> | null = null;

async function authenticate(): Promise<UserDto> {
  const webApp = getWebApp();
  const initData = webApp?.initData ?? '';

  const result = await request<{ user: UserDto }>('/api/auth/telegram', {
    method: 'POST',
    body: JSON.stringify({ initData, dev: !initData }),
  });

  return result.user;
}

export function ensureAuth(): Promise<UserDto> {
  if (!authPromise) {
    authPromise = authenticate().catch((error) => {
      authPromise = null;
      throw error;
    });
  }
  return authPromise;
}

export async function getFlights(): Promise<FlightDto[]> {
  const result = await request<{ flights: FlightDto[] }>('/api/flights');
  return result.flights;
}

export async function getStats(): Promise<StatsDto> {
  const result = await request<{ stats: StatsDto }>('/api/stats');
  return result.stats;
}

export async function getAirports(): Promise<AirportDto[]> {
  const result = await request<{ airports: AirportDto[] }>('/api/airports');
  return result.airports;
}

export interface NewFlight {
  flightNumber: string;
  depIata: string;
  arrIata: string;
  date: string;
  depTime: string;
  arrTime: string;
  seat?: string;
}

export async function createFlight(input: NewFlight): Promise<FlightDto> {
  const result = await request<{ flight: FlightDto }>('/api/flights', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return result.flight;
}

export async function deleteFlight(id: string): Promise<void> {
  await request<void>(`/api/flights/${id}`, { method: 'DELETE' });
}

export async function shareYear(year: number, image: string): Promise<void> {
  await request('/api/share/year', { method: 'POST', body: JSON.stringify({ year, image }) });
}

export async function logout(): Promise<void> {
  await request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' });
  authPromise = null;
}
